import {
  NOTIFICATION_EVENT_TYPES,
  NOTIFICATION_BADGE_TEXTS,
  NOTIFICATION_TONES
} from '@/constants/notification-constants';

/*
 * Apresentação de notificação: rótulo, ícone e TOM semântico do evento.
 *
 * O tom é resolvido em CSS pelos tokens `--tc-*` (classe `is-<tom>`), não por
 * nome de cor do Vuetify. Antes cada mapa devolvia string Material
 * ('deep-purple', 'blue lighten-3') — cor fixa que ignora o tenant e perde
 * contraste no tema escuro.
 *
 * Usado pelo painel do sino e pela tela de histórico; as duas superfícies
 * precisam falar a mesma língua visual.
 */

/* Guarda o NOME do texto, não o texto: NOTIFICATION_BADGE_TEXTS é lido na hora
   do uso, para o selo trocar junto com o idioma. */
const LABEL_MAP = {
  [NOTIFICATION_EVENT_TYPES.PRE_VENDA]: 'PRE_VENDA',
  [NOTIFICATION_EVENT_TYPES.PRE_VENDA_UPPER]: 'PRE_VENDA',
  [NOTIFICATION_EVENT_TYPES.PRE_VENDA_EXTERNA]: 'OPERACAO_EXTERNA',
  [NOTIFICATION_EVENT_TYPES.PRE_VENDA_CLIENTE]: 'OPERACAO_CLIENTE',
  [NOTIFICATION_EVENT_TYPES.PRE_VENDA_DOLSPAY]: 'OPERACAO_DOLSPAY',
  [NOTIFICATION_EVENT_TYPES.FICHA_ASSINADA]: 'FICHA_ASSINADA',
  [NOTIFICATION_EVENT_TYPES.FICHA_ASSINADA_UPPER]: 'FICHA_ASSINADA',
  [NOTIFICATION_EVENT_TYPES.RECEIVER_ENVIADO]: 'RECEIVER_ENVIADO',
  [NOTIFICATION_EVENT_TYPES.RECEIVER_APROVADO]: 'RECEIVER_APROVADO',
  [NOTIFICATION_EVENT_TYPES.RECEIVER_PENDENTE]: 'RECEIVER_PENDENTE',
  [NOTIFICATION_EVENT_TYPES.RECEIVER_FALHA]: 'RECEIVER_FALHA',
  [NOTIFICATION_EVENT_TYPES.DUIMP_ATUALIZADO]: 'DUIMP_ATUALIZADO',
  [NOTIFICATION_EVENT_TYPES.STATUS_BANCARIO]: 'STATUS_BANCARIO',
  [NOTIFICATION_EVENT_TYPES.RFI_COMPLIANCE]: 'RFI_COMPLIANCE',
  [NOTIFICATION_EVENT_TYPES.LIMITE_AUMENTO]: 'LIMITE_AUMENTO',
  [NOTIFICATION_EVENT_TYPES.COMISSAO_URGENTE]: 'COMISSAO_URGENTE',
  [NOTIFICATION_EVENT_TYPES.COBRANCA_DUIMP_PENDENTE]: 'COBRANCA_DUIMP_PENDENTE'
};

const typeOption = (value, badgeTextName) => ({
  value,
  get text() {
    return NOTIFICATION_BADGE_TEXTS[badgeTextName];
  }
});

const ICON_MAP = {
  [NOTIFICATION_EVENT_TYPES.PRE_VENDA]: 'PhCurrencyCircleDollar',
  [NOTIFICATION_EVENT_TYPES.PRE_VENDA_UPPER]: 'PhCurrencyCircleDollar',
  [NOTIFICATION_EVENT_TYPES.PRE_VENDA_EXTERNA]: 'PhCurrencyCircleDollar',
  [NOTIFICATION_EVENT_TYPES.PRE_VENDA_CLIENTE]: 'PhCurrencyCircleDollar',
  [NOTIFICATION_EVENT_TYPES.PRE_VENDA_DOLSPAY]: 'PhCurrencyBtc',
  [NOTIFICATION_EVENT_TYPES.FICHA_ASSINADA]: 'PhFiles',
  [NOTIFICATION_EVENT_TYPES.FICHA_ASSINADA_UPPER]: 'PhFiles',
  [NOTIFICATION_EVENT_TYPES.RECEIVER_ENVIADO]: 'PhPaperPlaneTilt',
  [NOTIFICATION_EVENT_TYPES.RECEIVER_APROVADO]: 'PhCheckCircle',
  [NOTIFICATION_EVENT_TYPES.RECEIVER_PENDENTE]: 'PhWarningCircle',
  [NOTIFICATION_EVENT_TYPES.RECEIVER_FALHA]: 'PhXCircle',
  [NOTIFICATION_EVENT_TYPES.DUIMP_ATUALIZADO]: 'PhClipboardText',
  [NOTIFICATION_EVENT_TYPES.STATUS_BANCARIO]: 'PhBank',
  [NOTIFICATION_EVENT_TYPES.RFI_COMPLIANCE]: 'PhShieldCheck',
  [NOTIFICATION_EVENT_TYPES.LIMITE_AUMENTO]: 'PhTrendUp',
  [NOTIFICATION_EVENT_TYPES.COMISSAO_URGENTE]: 'PhWarningCircle',
  [NOTIFICATION_EVENT_TYPES.COBRANCA_DUIMP_PENDENTE]: 'PhWarningCircle'
};

/* Lista para filtro de tipo na tela de histórico. As variantes MAIÚSCULAS são
   legado de registros antigos e não entram como opção — o filtro manda as
   duas grafias juntas para não esconder o histórico de antes da normalização. */
const NOTIFICATION_TYPE_OPTIONS = [
  typeOption(NOTIFICATION_EVENT_TYPES.PRE_VENDA, 'PRE_VENDA'),
  typeOption(NOTIFICATION_EVENT_TYPES.PRE_VENDA_EXTERNA, 'OPERACAO_EXTERNA'),
  typeOption(NOTIFICATION_EVENT_TYPES.PRE_VENDA_CLIENTE, 'OPERACAO_CLIENTE'),
  typeOption(NOTIFICATION_EVENT_TYPES.PRE_VENDA_DOLSPAY, 'OPERACAO_DOLSPAY'),
  typeOption(NOTIFICATION_EVENT_TYPES.FICHA_ASSINADA, 'FICHA_ASSINADA'),
  typeOption(NOTIFICATION_EVENT_TYPES.RECEIVER_ENVIADO, 'RECEIVER_ENVIADO'),
  typeOption(NOTIFICATION_EVENT_TYPES.RECEIVER_APROVADO, 'RECEIVER_APROVADO'),
  typeOption(NOTIFICATION_EVENT_TYPES.RECEIVER_PENDENTE, 'RECEIVER_PENDENTE'),
  typeOption(NOTIFICATION_EVENT_TYPES.RECEIVER_FALHA, 'RECEIVER_FALHA'),
  typeOption(NOTIFICATION_EVENT_TYPES.DUIMP_ATUALIZADO, 'DUIMP_ATUALIZADO'),
  typeOption(NOTIFICATION_EVENT_TYPES.STATUS_BANCARIO, 'STATUS_BANCARIO'),
  typeOption(NOTIFICATION_EVENT_TYPES.RFI_COMPLIANCE, 'RFI_COMPLIANCE'),
  typeOption(NOTIFICATION_EVENT_TYPES.LIMITE_AUMENTO, 'LIMITE_AUMENTO'),
  typeOption(NOTIFICATION_EVENT_TYPES.COMISSAO_URGENTE, 'COMISSAO_URGENTE'),
  typeOption(NOTIFICATION_EVENT_TYPES.COBRANCA_DUIMP_PENDENTE, 'COBRANCA_DUIMP_PENDENTE')
];

const LEGACY_ALIASES = {
  [NOTIFICATION_EVENT_TYPES.PRE_VENDA]: [NOTIFICATION_EVENT_TYPES.PRE_VENDA_UPPER],
  [NOTIFICATION_EVENT_TYPES.FICHA_ASSINADA]: [NOTIFICATION_EVENT_TYPES.FICHA_ASSINADA_UPPER]
};

export default {
  data() {
    return {
      notificationTypeOptions: NOTIFICATION_TYPE_OPTIONS
    };
  },
  methods: {
    getNotificationTone(eventType) {
      return NOTIFICATION_TONES[eventType] || 'neutral';
    },

    getNotificationToneClass(eventType) {
      return `is-${this.getNotificationTone(eventType)}`;
    },

    getNotificationLabel(eventType) {
      return NOTIFICATION_BADGE_TEXTS[LABEL_MAP[eventType]] || NOTIFICATION_BADGE_TEXTS.NOTIFICACAO;
    },

    getNotificationIcon(eventType) {
      return ICON_MAP[eventType] || 'PhBell';
    },

    /* Envia junto as grafias legadas do mesmo evento, senão filtrar por
       "Pré-venda" esconderia tudo que foi gravado como 'PRE_VENDA'. */
    expandNotificationTypes(values) {
      if (!values || !values.length) return [];

      return values.reduce((all, value) => all.concat(value, LEGACY_ALIASES[value] || []), []);
    }
  }
};
